import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, RefreshCw, Save, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const BranchesPage = () => {
  useAuth();
  const [branches, setBranches] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillingTx, setBackfillingTx] = useState(false);

  const headers = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const load = async () => {
    setLoading(true);
    try {
      const [br, cfg] = await Promise.all([
        axios.get(`${API}/branches`, headers()),
        axios.get(`${API}/branches/odoo-pos-configs`, headers()).catch(() => ({ data: [] })),
      ]);
      setBranches(br.data || []);
      setConfigs(cfg.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar sucursales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const assignConfigToBranch = async (configId, branchId) => {
    setSaving((prev) => ({ ...prev, [configId]: true }));
    try {
      // Remove config from any branch that currently owns it
      for (const b of branches) {
        if ((b.odoo_pos_config_ids || []).includes(configId) && b.id !== branchId) {
          await axios.patch(
            `${API}/branches/${b.id}`,
            { odoo_pos_config_ids: (b.odoo_pos_config_ids || []).filter((c) => c !== configId) },
            headers()
          );
        }
      }
      if (branchId) {
        const target = branches.find((b) => b.id === branchId);
        const newIds = Array.from(new Set([...(target?.odoo_pos_config_ids || []), configId]));
        await axios.patch(`${API}/branches/${branchId}`, { odoo_pos_config_ids: newIds }, headers());
      }
      toast.success("Mapeo actualizado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al mapear");
    } finally {
      setSaving((prev) => ({ ...prev, [configId]: false }));
    }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      "Esto asigna la sucursal correcta a las ventas ya guardadas. Idempotente. ¿Continuar?"
    )) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      // Loop up to 6 times to fully drain (endpoint returns up to 5000 per call)
      let total = { shopify_updated: 0, odoo_updated_fast: 0, odoo_updated_slow: 0 };
      for (let i = 0; i < 6; i++) {
        const res = await axios.post(`${API}/admin/backfill-sales-branches`, null, headers());
        total.shopify_updated += res.data.shopify_updated || 0;
        total.odoo_updated_fast += res.data.odoo_updated_fast || 0;
        total.odoo_updated_slow += res.data.odoo_updated_slow || 0;
        if (!res.data.odoo_updated_slow && !res.data.odoo_updated_fast && !res.data.shopify_updated) break;
      }
      setBackfillResult(total);
      toast.success(
        `Ventas actualizadas: Shopify=${total.shopify_updated}, Odoo=${total.odoo_updated_fast + total.odoo_updated_slow}`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en backfill");
    } finally {
      setBackfilling(false);
    }
  };

  const runTxBackfill = async () => {
    if (!window.confirm(
      "Esto asigna la sucursal 'Corporativo' a todos los gastos que aún no tienen sucursal. Idempotente. ¿Continuar?"
    )) return;
    setBackfillingTx(true);
    try {
      const res = await axios.post(
        `${API}/admin/backfill-transactions-branch?default_branch_id=corporativo`,
        null,
        headers()
      );
      toast.success(`Gastos actualizados: ${res.data.updated}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en backfill de gastos");
    } finally {
      setBackfillingTx(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="branches-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Sucursales</h1>
              <p className="text-sm text-[#6B7280]">
                Mapea las cajas de Odoo (pos.config) a cada sucursal. Shopify Online es una sucursal virtual.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={runBackfill}
              disabled={backfilling}
              variant="outline"
              className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
              data-testid="branches-backfill-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${backfilling ? "animate-spin" : ""}`} />
              {backfilling ? "Aplicando..." : "Backfill ventas existentes"}
            </Button>
            <Button
              onClick={runTxBackfill}
              disabled={backfillingTx}
              variant="outline"
              className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
              data-testid="branches-backfill-tx-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${backfillingTx ? "animate-spin" : ""}`} />
              {backfillingTx ? "Aplicando..." : "Backfill gastos → Corporativo"}
            </Button>
          </div>
        </div>

        {backfillResult && (
          <div className="p-3 rounded-lg bg-[#F0FDF4] border border-[#D1FAE5] text-sm text-[#065F46] space-y-1">
            <div>
              <strong>Backfill listo.</strong>
            </div>
            <div>
              Shopify: <strong>{backfillResult.shopify_updated}</strong> · Odoo (rápido):{" "}
              <strong>{backfillResult.odoo_updated_fast}</strong> · Odoo (con Odoo API):{" "}
              <strong>{backfillResult.odoo_updated_slow}</strong>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-[#6B7280]">Cargando...</div>
        ) : (
          <>
            {/* Branch cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {branches.map((b) => (
                <Card key={b.id} className="bg-white border-[#FECDD3] shadow-sm" data-testid={`branch-card-${b.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[#4A4A4A] font-['Manrope'] text-base flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: b.color }}
                      />
                      {b.name}
                      {b.is_online && <Badge className="bg-[#10B981] text-white">Online</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1 text-[#6B7280]">
                    <div>
                      Ventas registradas: <strong className="text-[#4A4A4A]">{b.sales_count ?? 0}</strong>
                    </div>
                    <div>
                      Cajas Odoo mapeadas:{" "}
                      <strong className="text-[#4A4A4A]">{(b.odoo_pos_config_ids || []).length}</strong>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Odoo pos.config mapping */}
            <Card className="bg-white border-[#FECDD3] shadow-sm">
              <CardHeader>
                <CardTitle className="text-[#4A4A4A] font-['Manrope'] text-base flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-[#8B5CF6]" />
                  Cajas de Odoo (pos.config)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {configs.length === 0 ? (
                  <div className="text-center text-[#6B7280] py-6 text-sm">
                    No se pudieron leer las cajas de Odoo. Revisa la configuración en Configuración › Odoo.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {configs.map((c) => {
                      const currentBranchId = c.current_branch?.branch_id || "__unassigned__";
                      return (
                        <div
                          key={c.id}
                          className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#FEF2F2]/40"
                          data-testid={`config-row-${c.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#4A4A4A]">{c.name}</div>
                            <div className="text-xs text-[#9CA3AF]">Odoo pos.config #{c.id}</div>
                          </div>
                          <Select
                            value={currentBranchId}
                            onValueChange={(v) =>
                              assignConfigToBranch(c.id, v === "__unassigned__" ? null : v)
                            }
                            disabled={saving[c.id]}
                          >
                            <SelectTrigger className="w-full sm:w-[260px] bg-white border-[#FECDD3]" data-testid={`config-select-${c.id}`}>
                              <SelectValue placeholder="Sin asignar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__unassigned__">Sin asignar</SelectItem>
                              {branches.filter((b) => !b.is_online).map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                  <span className="inline-flex items-center gap-2">
                                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                                    {b.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {saving[c.id] && <Save className="w-4 h-4 animate-pulse text-[#6B7280]" />}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-4 text-xs text-[#6B7280]">
                  Después de cambiar un mapeo, ejecuta <em>&quot;Backfill ventas existentes&quot;</em> para reasignar las ventas ya guardadas.
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default BranchesPage;
